import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { type FormQuestionState, type ModuleId, type ModuleResult } from '@pixacare/forms';
import { FormQuestionInputComponent } from '../../form-question/form-question-input/form-question-input.component';
import { FormGridCellErrorComponent } from '../form-grid-cell-error/form-grid-cell-error.component';
import { FormGridTriggerComponent } from '../form-grid-trigger/form-grid-trigger.component';
import { FormGridService } from '../services/form-grid.service';

@Component({
  selector: 'pxc-form-grid-question-cell',
  templateUrl: './form-grid-question-cell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormGridCellErrorComponent, FormGridTriggerComponent, FormQuestionInputComponent],
})
export class FormGridQuestionCellComponent {
  readonly entityKey = input.required<string>();
  readonly type = input.required<string>();
  readonly sectionId = input.required<string>();
  readonly question = input.required<FormQuestionState>();

  private readonly formGridService = inject(FormGridService);

  readonly isReadonly = computed(() => this.formGridService.mode(this.entityKey(), this.type()) === 'readonly');

  private readonly bridge = computed(() => this.formGridService.bridge(this.entityKey(), this.type()));

  readonly questionState = computed(() => this.bridge()?.questions()?.[this.question().id]);
  readonly control = computed(() => this.bridge()?.formGroup()?.get([this.sectionId(), this.question().id]));

  readonly moduleInputs = computed(() => this.formGridService.moduleInputs(this.entityKey(), this.type())());

  readonly questionModules = computed(() =>
    this.isReadonly() ? [] : this.formGridService.questionModules(this.entityKey(), this.type(), this.question().id),
  );

  private readonly hasAnswer = computed(() => {
    const answer = this.bridge()?.formAnswer()?.answers?.[this.question().id];
    return answer !== null && answer !== undefined;
  });

  readonly shouldRender = computed(() => {
    if (this.formGridService.isSectionHidden(this.entityKey(), this.type(), this.sectionId())) {
      return false;
    }
    const state = this.questionState();
    if (!state || state.isHidden || !this.control()) {
      return false;
    }
    return !this.isReadonly() || this.hasAnswer();
  });

  protected resetModule(event: { moduleId: ModuleId; targetId: string }): void {
    this.formGridService.resetModuleResult(this.entityKey(), this.type(), event.moduleId, event.targetId);
  }

  protected applyModule(event: { moduleId: ModuleId; targetId: string; result: ModuleResult }): void {
    this.formGridService.applyModuleResult(this.entityKey(), this.type(), event.moduleId, event.targetId, event.result);
  }
}
